import React, { useState } from 'react';
import Button from './ui/Button';
import Textarea from './ui/Textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Loader2, Sparkles, Copy, Check, RefreshCw } from 'lucide-react';
import api from '../lib/api';

interface AIMessageGeneratorProps {
  endpoint: string;
  payload: any;
  onAccept: (message: string) => void;
  title?: string;
  description?: string;
  placeholder?: string;
  buttonText?: string;
  showSuggestions?: boolean;
}

interface GeneratedMessage {
  subject?: string;
  body: string;
  suggestions?: string[];
  sentiment?: 'positive' | 'neutral' | 'negative';
}

export const AIMessageGenerator: React.FC<AIMessageGeneratorProps> = ({
  endpoint,
  payload,
  onAccept,
  title = 'AI Message Assistant',
  description = 'Generate a professional message with AI assistance',
  placeholder = 'AI-generated message will appear here...',
  buttonText = 'Generate with AI',
  showSuggestions = true,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedMessage, setGeneratedMessage] = useState<GeneratedMessage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [editedMessage, setEditedMessage] = useState('');

  const generateMessage = async () => {
    setIsGenerating(true);
    setError(null);
    setGeneratedMessage(null);

    try {
      const response = await api.post(endpoint, payload);
      const message: GeneratedMessage = response.data;
      setGeneratedMessage(message);
      setEditedMessage(message.body);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate message');
      console.error('AI generation error:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    if (editedMessage) {
      await navigator.clipboard.writeText(editedMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleAccept = () => {
    if (editedMessage) {
      onAccept(editedMessage);
      // Reset state
      setGeneratedMessage(null);
      setEditedMessage('');
      setError(null);
    }
  };

  return (
    <Card className="border-purple-200 bg-purple-50/50">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-purple-600" />
          <CardTitle className="text-lg">{title}</CardTitle>
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Generate Button */}
        {!generatedMessage && (
          <Button
            onClick={generateMessage}
            disabled={isGenerating}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                {buttonText}
              </>
            )}
          </Button>
        )}

        {/* Error Display */}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Generated Message Display */}
        {generatedMessage && (
          <div className="space-y-4">
            {/* Subject (if present) */}
            {generatedMessage.subject && (
              <div>
                <label className="text-sm font-medium text-gray-700">Subject:</label>
                <p className="mt-1 text-sm font-semibold">{generatedMessage.subject}</p>
              </div>
            )}

            {/* Message Body */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">Message:</label>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={copyToClipboard}
                    className="h-8"
                  >
                    {copied ? (
                      <>
                        <Check className="h-3 w-3 mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={generateMessage}
                    disabled={isGenerating}
                    className="h-8"
                  >
                    <RefreshCw className="h-3 w-3 mr-1" />
                    Regenerate
                  </Button>
                </div>
              </div>
              <Textarea
                value={editedMessage}
                onChange={(e) => setEditedMessage(e.target.value)}
                placeholder={placeholder}
                className="min-h-[200px] font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                You can edit the message before accepting it
              </p>
            </div>

            {/* Suggestions */}
            {showSuggestions && generatedMessage.suggestions && generatedMessage.suggestions.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-700">AI Suggestions:</label>
                <ul className="mt-2 space-y-1 text-sm text-gray-600">
                  {generatedMessage.suggestions.map((suggestion, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-purple-600 mt-0.5">•</span>
                      <span>{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Sentiment Indicator */}
            {generatedMessage.sentiment && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-500">Tone:</span>
                <span
                  className={`font-medium ${
                    generatedMessage.sentiment === 'positive'
                      ? 'text-green-600'
                      : generatedMessage.sentiment === 'negative'
                      ? 'text-red-600'
                      : 'text-gray-600'
                  }`}
                >
                  {generatedMessage.sentiment.charAt(0).toUpperCase() + generatedMessage.sentiment.slice(1)}
                </span>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button
                onClick={handleAccept}
                className="flex-1 bg-purple-600 hover:bg-purple-700"
              >
                Use This Message
              </Button>
              <Button
                onClick={() => {
                  setGeneratedMessage(null);
                  setEditedMessage('');
                  setError(null);
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
